/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from 'providers/AccountProvider';
import { useNavigate } from 'react-router-dom';
import { removeCookie, SESSION_COOKIE_KEY } from 'services/cookies';
import { Spin, notification } from 'antd';
import { motion } from 'framer-motion';
import GastronomyIcon from 'assets/icons/gastronomy_icon.svg';
import './styles.scss';
import { axios } from 'startup';
import config from 'config';
import { IGastronomyDTO } from 'DTOModels/gastronomyDTO';
import { gastronomyMotion, gastronomyListItemMotion } from './animation';
import GastronomyListItem from './components/GastronomyListItem';

const top = 10;

function Gastronomy() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [hasMore, setHasMore] = useState(true);
    const { setAccount } = useAccount();
    const [items, setItems] = useState<IGastronomyDTO[]>([]);

    const logout = useCallback(() => {
        // timeout just to add a bit of delay
        setTimeout(() => {
            removeCookie(SESSION_COOKIE_KEY);
            if (setAccount) setAccount(undefined);
            navigate('/login');
        }, 250);
        notification.error({
            message: t('general.ERROR'),
            description: t('general.SUSPICIOUS_ACTIVITY'),
        });
    }, []);

    const fetchList = useCallback(() => {
        const currentDate = new Date();
        setHasMore(true);
        setLoading(true);
        axios
            .get(`${config.API_URL}/gastronomy?top=${top}&date=${currentDate}`)
            .then(response => {
                setItems(
                    response.data.Gastronomy.map((item: IGastronomyDTO) => ({
                        ...item,
                    })),
                );
                if (response.data.Total <= top) setHasMore(false);
                setLoading(false);
            })
            .catch(() => {
                // force logout
                logout();
            });
    }, []);

    const fetchMore = () => {
        const lastId = items[items.length - 1].Id;
        const currentDate = new Date();
        axios
            .get(
                `${config.API_URL}/gastronomy?top=${top}&lastId=${lastId}&date=${currentDate}`,
            )
            .then(response => {
                setItems(m => [
                    ...m,
                    ...response.data.Gastronomy.filter(
                        (a: IGastronomyDTO) => !items.some(e => e.Id === a.Id),
                    ),
                ]);
                if (response.data.Total <= top) setHasMore(false);
            })
            .catch(() => {
                // force logout
                logout();
            });
    };

    useEffect(() => {
        fetchList();
    }, []);

    return (
        <div className='gastronomy'>
            <div className='gastronomy__content'>
                {loading && (
                    <div className='gastronomy__content__empty'>
                        <Spin className='' size='large' />
                    </div>
                )}
                {!loading && items.length === 0 && (
                    <motion.div
                        {...gastronomyMotion}
                        className='gastronomy__content__empty'
                    >
                        <img src={GastronomyIcon} alt='Gastronomy Icon' />
                        <p>
                            {t(
                                'accommodationAndGastronomy.NO_GASTRONOMY_REGISTERED',
                            )}
                        </p>
                    </motion.div>
                )}
                {!loading && items.length > 0 && (
                    <motion.div
                        {...gastronomyMotion}
                        className='gastronomy__content__list'
                    >
                        {items.map(
                            (gastronomyItem: IGastronomyDTO, index: number) => {
                                return (
                                    <motion.div
                                        {...gastronomyListItemMotion}
                                        className='gastronomy__content__list__item'
                                        key={index}
                                    >
                                        <GastronomyListItem
                                            gastronomy={gastronomyItem}
                                        />
                                    </motion.div>
                                );
                            },
                        )}
                    </motion.div>
                )}
            </div>
            {!loading && items.length > 0 && hasMore && (
                <button
                    type='button'
                    className='gastronomy__button'
                    onClick={() => {
                        fetchMore();
                    }}
                >
                    {t('general.SEE_MORE')}
                </button>
            )}
        </div>
    );
}

export default Gastronomy;
